import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Navigate, useLocation } from 'react-router-dom';
import { RootState, AppDispatch } from '../store';
import { fetchProfile } from '../store/workerProfileSlice';
import { FaSpinner } from 'react-icons/fa';

interface RequireProfileProps {
  children: React.ReactElement;
}

const RequireProfile: React.FC<RequireProfileProps> = ({ children }) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { user } = useSelector((state: RootState) => state.auth);
  const { profile, loading, error } = useSelector((state: RootState) => state.workerProfile);

  useEffect(() => {
    if (user?.role === 'worker' && !profile && !loading && !error) {
      dispatch(fetchProfile());
    }
  }, [dispatch, user, profile, loading, error]);

  // Only enforce for workers
  if (user?.role !== 'worker') return children;

  // Keep the worker in a stable loading state while the first profile request
  // starts; otherwise the initial empty Redux state briefly redirects them.
  if (!profile && !error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <FaSpinner className="text-4xl animate-spin text-blue-600" />
      </div>
    );
  }

  // If error fetching profile, don't block (could be temporary)
  if (error && !profile) return children;

  // Union status, rates, certifications, and auto-apply are progressive
  // details, but both matching inputs must be present before activation.
  const hasPrimaryRole = typeof profile?.primary_role === 'string'
    && profile.primary_role.trim().length > 0;
  const hasLocation = typeof profile?.location === 'string'
    && profile.location.trim().length > 0;
  const needsOnboarding = !hasPrimaryRole || !hasLocation;

  if (needsOnboarding) {
    return <Navigate to="/profile/setup" state={{ from: location.pathname, onboarding: true }} replace />;
  }

  return children;
};

export default RequireProfile;
